import {DotLottiePlayer, type DotLottieCommonPlayer} from '@dotlottie/react-player'
import '@dotlottie/react-player/dist/index.css'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {useInView} from 'framer-motion'
import type {BentoGridSliceDefaultPrimaryCardsItem} from 'prismicio-types'
import type {FC} from 'react'
import {memo, useEffect, useRef} from 'react'
import {TextLink} from 'src/common/TextLink'
import {Tag} from './Tag'

type BentoCardProps = {
  card: BentoGridSliceDefaultPrimaryCardsItem
  spanTwoColumns?: boolean
}

/**
 * BentoCard - A reusable card component for the BentoGrid slice
 *
 * Features:
 * - Supports title, rich text content, and image/Lottie animation
 * - Can span 1 or 2 columns in a grid
 * - Different styling for first card (larger title, more spacing)
 * - Responsive image handling with thumbnails
 * - Lottie animation support (takes precedence over images when provided)
 * - Lottie animations play only when 50% of the card is visible (using Intersection Observer)
 * - Optional quote section with author image (hidden on mobile)
 * - Optional tag with icon
 * - Memoized to prevent unnecessary re-renders in lists
 */
const BentoCardComponent: FC<BentoCardProps> = ({card, spanTwoColumns = false}) => {
  const hasQuote = isFilled.richText(card.quote)
  const desktopImageField = getDesktopImageField(card)
  const mobileLottieRef = useRef<DotLottieCommonPlayer>(null)
  const desktopLottieRef = useRef<DotLottieCommonPlayer>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(cardRef, {amount: 0.5, once: true})

  // Trigger Lottie animation play when card is in view
  useEffect(() => {
    if (isInView) {
      if (mobileLottieRef.current) {
        mobileLottieRef.current.play()
      }

      if (desktopLottieRef.current) {
        desktopLottieRef.current.play()
      }
    }
  }, [isInView])

  return (
    <div
      ref={cardRef}
      className={cn('relative flex flex-col gap-6 rounded-lg bg-moss-white p-4 md:min-w-[340px] md:p-6', {
        'md:col-span-2': spanTwoColumns,
        'md:gap-10': spanTwoColumns && !card.text_center && !hasQuote,
        'md:gap-16': spanTwoColumns && card.text_center,
        'flex-col': !hasQuote,
        'md:flex-row md:gap-6': hasQuote,
      })}
    >
      {/* Content */}
      <div
        className={cn('flex w-full flex-col gap-4', {
          'max-w-[593px]': spanTwoColumns && !card.text_center,
          'text-center': card.text_center,
          'md:h-full md:justify-between': hasQuote,
        })}
      >
        <div
          className={cn('flex flex-col gap-4', {
            'gap-4': !isFilled.keyText(card.tag),
            'gap-6': isFilled.keyText(card.tag),
          })}
        >
          {isFilled.keyText(card.blue_eyebrow) && (
            <p className="text-12 font-semibold uppercase leading-normal tracking-[0.06em] text-moss-blue">
              {card.blue_eyebrow}
            </p>
          )}
          {isFilled.keyText(card.tag) && (
            <div className="w-fit self-center">
              <Tag icon={card.tag_icon} text={card.tag} />
            </div>
          )}
          {isFilled.richText(card.title) && (
            <PrismicRichText
              field={card.title}
              components={{
                heading3: ({children}) => (
                  <h3
                    className={cn('text-20 font-medium leading-6 text-moss-black', {
                      'md:text-h3 md:leading-10': spanTwoColumns,
                      'md:text-h4': !spanTwoColumns,
                    })}
                  >
                    {children}
                  </h3>
                ),
                heading2: ({children}) => (
                  <h2 className="md:text-h2 text-28 font-medium leading-8 text-moss-black max-md:tracking-[-.28px]">
                    {children}
                  </h2>
                ),
                strong: ({children}) => <strong className="font-medium text-moss-black/[60%]">{children}</strong>,
              }}
            />
          )}
          {isFilled.richText(card.content) && (
            <PrismicRichText
              field={card.content}
              components={{
                paragraph: ({children}) => (
                  <p
                    className={cn('md:text-tg-body-l text-16 leading-[22px] text-moss-grey-400', {
                      'max-w-[439px]': spanTwoColumns,
                      'self-center': spanTwoColumns && card.text_center,
                      'max-w-[471px]': !spanTwoColumns,
                    })}
                  >
                    {children}
                  </p>
                ),
              }}
            />
          )}
          {isFilled.link(card.link) && (
            <TextLink
              field={card.link}
              label={card.link.text || undefined}
              color="moss-grey-400"
              className="text-18 font-normal normal-case"
              arrowFirst
            />
          )}
        </div>
        {hasQuote && (
          <div className="flex items-end gap-4 rounded bg-moss-grey-100 p-4 max-lg:hidden" id="quote">
            <PrismicNextImage
              field={card.author_image}
              className="aspect-square h-[100px] max-h-[100px] min-w-[100px] max-w-[100px] rounded"
              fallbackAlt=""
            />
            <div className="flex max-w-[386px] flex-col justify-between gap-2.5">
              <PrismicRichText
                field={card.quote}
                components={{
                  heading6: ({children}) => (
                    <div className="text-tg-body-m flex items-center gap-3 text-moss-black">{children}</div>
                  ),
                  paragraph: ({children}) => <span className="text-button-xs flex items-center gap-3">{children}</span>,
                  em: () => <div className="h-[3.43px] w-[3.43px] bg-moss-grey-300" />,
                  strong: ({children}) => <span className="text-moss-grey-300">{children}</span>,
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Image or Lottie Animation */}
      {isFilled.link(card.lottie_animation) ? (
        <div
          className={cn('w-full overflow-hidden rounded bg-[linear-gradient(102deg,#DEDEDE_-41.59%,#EEE_84.97%)]', {
            'border border-moss-white/10': spanTwoColumns && !hasQuote,
            'max-w-[540px]': spanTwoColumns && hasQuote,
          })}
        >
          {isFilled.link(card.mobile_lottie_animation) ? (
            <>
              {/* Mobile Lottie - shown only on mobile */}
              <DotLottiePlayer
                loop
                src={card.mobile_lottie_animation.url!}
                ref={mobileLottieRef}
                className="h-auto w-full md:!hidden"
              />
              {/* Desktop Lottie - shown only on desktop */}
              <DotLottiePlayer
                loop
                src={card.lottie_animation.url!}
                ref={desktopLottieRef}
                className="!hidden h-auto w-full md:!block"
              />
            </>
          ) : (
            <DotLottiePlayer loop src={card.lottie_animation.url!} ref={desktopLottieRef} className="h-auto w-full" />
          )}
        </div>
      ) : (
        isFilled.image(card.image) && (
          <div
            className={cn('w-full overflow-hidden rounded bg-[linear-gradient(102deg,#DEDEDE_-41.59%,#EEE_84.97%)]', {
              'border border-moss-white/10': spanTwoColumns,
              'max-w-[540px]': !spanTwoColumns || hasQuote,
            })}
          >
            {card.image.mobile?.url ? (
              <>
                {/* Mobile version - shown only on mobile */}
                <PrismicNextImage
                  quality={100}
                  field={card.image.mobile}
                  className="h-auto w-full object-cover md:hidden"
                  fallbackAlt=""
                />
                {/* Desktop version - shown only on desktop for two-column spans, or always use mobile thumbnail for single column */}
                {spanTwoColumns ? (
                  <PrismicNextImage
                    quality={100}
                    field={desktopImageField}
                    className="hidden h-auto w-full object-cover md:block"
                    fallbackAlt=""
                  />
                ) : (
                  <PrismicNextImage
                    quality={100}
                    field={card.image.mobile}
                    className="hidden h-auto w-full object-cover md:block"
                    fallbackAlt=""
                  />
                )}
              </>
            ) : (
              <PrismicNextImage
                quality={100}
                field={desktopImageField}
                className="h-auto w-full object-cover"
                fallbackAlt=""
              />
            )}
          </div>
        )
      )}
      {/* Decorative inner border */}
      <div className="pointer-events-none absolute inset-0 rounded-lg border border-moss-black/10" />
    </div>
  )
}

BentoCardComponent.displayName = 'BentoCard'

export const BentoCard = memo(BentoCardComponent)

const getDesktopImageField = (card: BentoGridSliceDefaultPrimaryCardsItem) => {
  switch (true) {
    case isFilled.keyText(card.tag):
      return card.image.banner
    case isFilled.richText(card.quote):
      return card.image.mobile
    default:
      return card.image
  }
}
